/**
 * Create rule-based preset - professional horizontal layout
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState, useMemo } from 'react';
import { Filter, Globe, Info, Save, X } from 'lucide-react';

import { Button, Checkbox, Input, Switch, Tooltip } from '@/components/ui';
import {
  ShortlinkOptions,
  StaticTagsPickerSection,
} from '@/features/smswiser-campaigns/components/drop-modals';
import {
  DEFAULT_CLICK_ID_STRUCTURE,
  DEFAULT_DOMAIN_SCHEME,
  listRuleFromGroups,
  toClickIdStructure,
  toDomainScheme,
} from '@trafficpulse/shared';
import type { PresetsModalCatalog, PresetTemplatePayload } from './shared';
import { GroupSelect } from './shared';
import { RuleBuilder } from './rule';
import { PresetFoldersPicker } from './folders';
import { backdropClick } from '@/components/ui';

export interface CreateRuleBasedPresetModalProps {
  countryKey: string;
  catalog: PresetsModalCatalog;
  availableTags: string[];
  availableGroupNames?: string[];
  tagColors?: Record<string, string>;
  isBusy: boolean;
  onClose: () => void;
  onCreate: (payload: {
    name: string;
    groupName?: string;
    presetType: 'rule-based';
    isPublic?: boolean;
    listRule: Record<string, unknown>;
    template: PresetTemplatePayload;
    folderIds?: string[];
  }) => void;
}

const HOW_IT_WORKS =
  'One drop per matching list. Tags in a group = OR (any). Groups = AND (all). Example: (New OR Unused) AND (High Profit).';

export const CreateRuleBasedPresetModal: FC<CreateRuleBasedPresetModalProps> = ({
  countryKey,
  catalog,
  availableTags,
  availableGroupNames = [],
  tagColors,
  isBusy,
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [groups, setGroups] = useState<string[][]>([[]]);
  const [amount, setAmount] = useState(150);
  const [shortLinkTldValue, setShortLinkTldValue] = useState('');
  const [staticTagId, setStaticTagId] = useState<string | null>(null);
  const [forceSend, setForceSend] = useState(false);
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState(false);
  const [clickIdStructure, setClickIdStructure] = useState(DEFAULT_CLICK_ID_STRUCTURE);
  const [domainScheme, setDomainScheme] = useState(DEFAULT_DOMAIN_SCHEME);
  const [presetFolderIds, setPresetFolderIds] = useState<string[]>([]);

  const listRule = useMemo(() => {
    const valid = groups.filter((g) => g.length > 0 && g.some((t) => t.trim()));
    if (valid.length === 0) return null;
    return listRuleFromGroups(valid);
  }, [groups]);

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed || !listRule) return;
    const template: PresetTemplatePayload = {
      amount,
      shortLinkTldValue: shortLinkTldValue.trim() || undefined,
      staticTagId: staticTagId ?? undefined,
      forceSend,
      useOnlyNewDomains,
      clickIdStructure,
      domainScheme,
    };
    onCreate({
      name: trimmed,
      groupName: groupName.trim() || undefined,
      presetType: 'rule-based',
      isPublic,
      listRule: listRule as unknown as Record<string, unknown>,
      template,
      folderIds: presetFolderIds.length ? presetFolderIds : undefined,
    });
    onClose();
  };

  const canCreate = name.trim().length > 0 && listRule != null && amount >= 1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <Filter className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight">Create rule-based preset</h2>
              <p className="text-xs text-muted-foreground">Match lists by tags · One drop per match</p>
            </div>
            <Tooltip content={HOW_IT_WORKS}>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="How it works"
              >
                <Info className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden sm:flex-row">
          <section className="flex min-w-0 flex-1 flex-col border-b border-border p-5 sm:border-b-0 sm:border-r">
            <h3 className="mb-4 text-sm font-semibold">Rule</h3>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <RuleBuilder
                groups={groups}
                availableTags={availableTags}
                tagColors={tagColors}
                onChange={setGroups}
              />
            </div>
          </section>

          <aside className="flex w-full shrink-0 flex-col gap-4 border-t border-border bg-muted/20 p-5 sm:w-72 sm:border-t-0">
            <h3 className="text-sm font-medium">Settings</h3>
            <div className="flex flex-col gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Preset name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. New High Profit"
                  maxLength={120}
                  className="h-9"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Group (optional)</label>
                <GroupSelect
                  value={groupName}
                  onChange={setGroupName}
                  availableGroupNames={availableGroupNames}
                  className="h-9"
                />
              </div>
              <label className="flex cursor-pointer items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">Public</span>
                <Switch checked={isPublic} onCheckedChange={setIsPublic} />
              </label>
              <PresetFoldersPicker
                countryKey={countryKey}
                value={presetFolderIds}
                onChange={setPresetFolderIds}
                disabled={isBusy}
              />
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Amount per drop</label>
                <Input
                  type="number"
                  min={1}
                  max={50000}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value) || 1)}
                  className="h-9"
                />
              </div>
              {catalog.tlds.length > 0 && (
                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    Shortlink TLD
                    <Tooltip content="Stored by value (e.g. .co, .me). When applying in another country, the same TLD type is used if available.">
                      <button
                        type="button"
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label="TLD info"
                      >
                        <Info className="h-3 w-3" />
                      </button>
                    </Tooltip>
                  </label>
                  <select
                    value={shortLinkTldValue}
                    onChange={(e) => setShortLinkTldValue(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="">Select TLD (optional at apply)</option>
                    {catalog.tlds.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <StaticTagsPickerSection
                mode="single"
                selectedId={staticTagId}
                onChange={setStaticTagId}
                compact
              />
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Drop options
                </h4>
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox checked={forceSend} onCheckedChange={(c) => setForceSend(Boolean(c))} />
                  <span>Include duplicates</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox
                    checked={useOnlyNewDomains}
                    onCheckedChange={(c) => setUseOnlyNewDomains(Boolean(c))}
                    disabled={amount > 10000}
                  />
                  <span>New domains only</span>
                </label>
                <ShortlinkOptions
                  compact
                  clickIdStructure={clickIdStructure}
                  setClickIdStructure={(v) => setClickIdStructure(toClickIdStructure(v))}
                  domainScheme={domainScheme}
                  setDomainScheme={(v) => setDomainScheme(toDomainScheme(v))}
                />
              </div>
            </div>
          </aside>
        </div>

        <footer className="flex shrink-0 justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate || isBusy}>
            <Save className="h-3.5 w-3.5" />
            Create preset
          </Button>
        </footer>
      </div>
    </div>
  );
};
